<script setup lang="ts">
const props = defineProps<{ src: string, altText?: string, shape: FrameShape, dir?: Direction, filledBackground?: string }>()

// Static values below are copied directly from the Figma design
const FRAME = {
  decagon: {
    path: 'M258.525 81.1123L244.93 39.223C243.678 35.3618 241.236 31.9952 237.947 29.6116L202.296 3.7135C199.013 1.32992 195.061 0.0449732 191.007 0.0449732H149.69C149.266 0.0192742 148.842 0 148.411 0H20.5592C9.20665 0 0 9.20665 0 20.5592V153.551C0 164.904 9.20665 174.11 20.5592 174.11H148.411C148.842 174.11 149.266 174.091 149.69 174.065H191.007C195.061 174.065 199.019 172.78 202.296 170.397L237.947 144.499C241.23 142.109 243.678 138.749 244.93 134.881L258.531 92.9595C259.784 89.1047 259.784 84.9543 258.531 81.0995L258.525 81.1123Z',
    size: [259.471, 174.11],
  },
  arrow: {
    path: 'M256.805 77.4128L217.672 9.6469C214.242 3.7059 207.903 0.0449589 201.037 0.0449589H142.642C142.218 0.0192681 141.794 0 141.364 0H13.5583H20.5526C9.20373 0.0064227 0 9.21015 0 20.5591V153.509C0 164.858 9.20373 174.062 20.5526 174.062H13.5583H141.37C141.8 174.062 142.224 174.042 142.648 174.017H201.043C207.903 174.017 214.248 170.356 217.678 164.415L256.812 96.6231C260.241 90.6821 260.241 83.3602 256.812 77.4192L256.805 77.4128Z',
    size: [259.471, 174.11],
  },
  hexagon: {
    path: 'M159.344 10.7363C155.797 4.59293 149.241 0.808594 142.147 0.808594H61.2476C54.1535 0.808594 47.5981 4.59293 44.0504 10.7363L3.58684 80.8062C0.0387597 86.9503 0.0381577 94.5206 3.58526 100.665L44.0509 170.765C47.5982 176.91 54.1544 180.695 61.2497 180.695H142.145C149.24 180.695 155.796 176.91 159.344 170.765L199.809 100.665C203.356 94.5206 203.356 86.9503 199.808 80.8062L159.344 10.7363Z',
    size: [203, 181],
  },
  decagonFilled: {
    path: 'M128.478 4.33003C125.085 1.86578 121 0.538574 116.807 0.538574H71.2588C67.0658 0.538574 62.9803 1.86577 59.5878 4.33001L22.7348 31.0994C19.3395 33.5656 16.8128 37.044 15.5172 41.0355L1.46198 84.3359C0.168791 88.3199 0.168419 92.6108 1.46091 96.595L15.5177 139.926C16.813 143.919 19.3401 147.398 22.7363 149.865L59.5878 176.634C62.9803 179.098 67.0658 180.425 71.2588 180.425H116.807C121 180.425 125.085 179.098 128.478 176.634L165.329 149.865C168.725 147.398 171.253 143.919 172.548 139.926L186.605 96.595C187.897 92.6108 187.897 88.3199 186.604 84.3359L172.548 41.0354C171.253 37.044 168.726 33.5656 165.331 31.0994L128.478 4.33003Z',
    size: [188, 181],
  },
} as const satisfies Record<FrameShape, { path: string, size: [number, number] }>

const v = computed(() => FRAME[props.shape])

// So that the id (which is put in the DOM) is not duplicated when multiple instances of the component are used
const id = useId()
</script>

<template>
  <svg v-if="v.path" :class="{ 'transform -scale-x-100': dir === 'rtl' }" :role="altText ? 'img' : undefined" xmlns="http://www.w3.org/2000/svg" :viewBox="`0 0 ${v.size[0]} ${v.size[1]}`" fill="none">
    <title v-if="altText" v-text="altText" />
    <path :d="v.path" :fill="`url(#${id})`" />
    <defs>
      <pattern :id="id" patternUnits="userSpaceOnUse" :width="v.size[0]" :height="v.size[1]">
        <rect v-if="filledBackground" :width="v.size[0]" :height="v.size[1]" :fill="filledBackground" />

        <image :href="src" class="bg-white" :src="src" :width="v.size[0]" :height="v.size[1]" preserveAspectRatio="xMidYMid slice" />
      </pattern>
    </defs>
  </svg>
</template>
